import { ChangeDetectorRef, Component, HostBinding } from "@angular/core";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router
} from "@angular/router";
import { filter } from "rxjs/operators";
import { AuthenticationService } from "../../../_services/authentication.service";
import { TranslateService } from "@ngx-translate/core";
import { AppState } from "src/app/state/app.state";
import { NavigatorState } from "src/app/state/navigator.state";
import { LocaleService, UserService, ILanguage } from "../../../_services";
import { LxmAppModule, UserAction } from "src/app/enum";
import { getPackagingDataLink, LINKS, LOCAL_STORAGE_KEY, RETAILER_ID } from "src/app/config";
import { LocalStorage } from "src/app/_helpers";
import { appSettings } from "src/app/app.settings";
import { FormControl, FormGroup } from "@angular/forms";
import { Subscription } from "rxjs";
import { AppLanguage } from "src/app/enum/app-language";
import { SubscriptionPlan } from "src/app/enum/subscription-plan";
import { BREAKPOINTS } from "src/app/_helpers/responsive";

const ROUTER_ACTIVE_OPTIONS = {
  paths: "subset" as const,
  queryParams: "ignored" as const,
  fragment: "exact" as const,
  matrixParams: "ignored" as const
};

@Component({
  selector: "navigator",
  templateUrl: "./navigator.component.html",
  styleUrls: ["./navigator.scss"]
})
export class NavigatorComponent {
  @HostBinding("class.navigator-collapsed") public get collapsed() {
    return this.navigator.collapsed;
  }

  @HostBinding("class.item-fullscreen") public get isAnyItemFullscreen() {
    return this.navigator.isAnyItemFullscreen;
  }

  public UserAction = UserAction;
  public LxmAppModule = LxmAppModule;

  public form: FormGroup;

  public showTenantsFilter = false;

  /** Path we are navigating to; only the matching link shows active until navigation completes */
  private _pendingNavigationPath: string | null = null;

  private _routerEventsSubscription: Subscription;

  constructor(
    private _router: Router,
    public appState: AppState,
    public locale: LocaleService,
    public navigator: NavigatorState,
    public translate: TranslateService,
    private _authenticationService: AuthenticationService,
    private _userService: UserService,
    private _translateService: TranslateService,
    private _cd: ChangeDetectorRef
  ) {
    navigator.hasBuyerPimExtensions = appState.hasModule(
      LxmAppModule.BuyerPimExtensions
    );
  }

  public tenants = [];

  public get currentView() {
    return this.navigator.currentView;
  }

  public get version() {
    return appSettings?.version;
  }

  public get tenantId() {
    return this._authenticationService.currentTenantId.value;
  }

  public get showProductReactiveLinks() {
    if (this.collapsed) return false;
    return (
      this.navigator.showProductReactiveLinks &&
      this._router.isActive("products", ROUTER_ACTIVE_OPTIONS)
    );
  }

  public get showMarketReactiveLinks() {
    if (this.collapsed) return false;
    return (
      this.navigator.showProductReactiveLinks &&
      this._router.isActive("market", ROUTER_ACTIVE_OPTIONS)
    );
  }

  public languageValueCompare(v1: ILanguage, v2: ILanguage) {
    return v1.id === v2.id;
  }

  public get languageOptions() {
    const uiLanguages = this.appState.userSettings?.uiLanguages;
    if (uiLanguages?.length > 0) {
      return uiLanguages;
    }
    return this.locale.languageOptions;
  }

  public getLanguageIcon(languageId: AppLanguage) {
    switch (languageId) {
      case AppLanguage.Estonian:
        return "est";
      case AppLanguage.English:
        return "gbr";
      case AppLanguage.Tallink: // TAL/Tallink
        return "tallink";
      case AppLanguage.German:
        return "deu";
      default:
        return "global";
    }
  }

  public toggleView() {
    this.currentView === 0
      ? (this.navigator.currentView = 1)
      : (this.navigator.currentView = 0);
  }

  public collapse(newState = !this.navigator.collapsed) {
    let updatedState = this.isSmallerScreen || newState;
    this.navigator.collapsed = updatedState;
    LocalStorage.set(LOCAL_STORAGE_KEY.NAVIGATOR_OPEN, updatedState);
    setTimeout((_) => {
      window.dispatchEvent(new Event("resize"));
    });
  }

  public get instructionsLink() {
    const lang = this._translateService.currentLang;

    switch (this.tenantId) {
      case RETAILER_ID.SELVER:
        const hasRight = this._authenticationService.hasRight([
          UserAction.ViewClvs
        ]);
        return hasRight ? LINKS.SELVER_MANUAL : null;
      default:
        switch (lang) {
          case "et":
            return LINKS.LXM_MANUAL_ET;
          default:
            return LINKS.LXM_MANUAL_EN;
        }
    }
  }

  public get isSelver() {
    return this.tenantId === RETAILER_ID.SELVER;
  }

  public get userManagementLink() {
    const lang = this._translateService.currentLang;

    switch (this.tenantId) {
      case RETAILER_ID.SELVER:
        return null;
      default:
        switch (lang) {
          case "et":
            return LINKS.USER_MANAGEMENT_ET;
          default:
            return LINKS.USER_MANAGEMENT_EN;
        }
    }
  }

  public get pujuLink() {
    switch (this.tenantId) {
      case RETAILER_ID.SELVER:
        return LINKS.SELVER_PUJU;
      default:
        return null;
    }
  }

  public get supplierFrVgInstructionsLink() {
    const lang = this._translateService.currentLang;

    switch (lang) {
      case "et":
        return LINKS.SUPPLIER_FRVG_ET;
      default:
        return LINKS.SUPPLIER_FRVG_EN;
    }
  }

  public get sellerStoreManualLink() {
    const lang = this._translateService.currentLang;

    switch (lang) {
      case "et":
        return LINKS.SELLER_STORE_MANUAL_ET;
      default:
        return LINKS.SELLER_STORE_MANUAL_EN;
    }
  }

  public get buyerStoreManualLink() {
    const lang = this._translateService.currentLang;

    switch (lang) {
      case "et":
        return LINKS.BUYER_STORE_MANUAL_ET;
      default:
        return LINKS.BUYER_STORE_MANUAL_EN;
    }
  }

  public get packagingDataInstructionsLink() {
    return getPackagingDataLink(this._translateService.currentLang);
  }

  public get ordersInstructionsLink() {
    switch (this.tenantId) {
      case RETAILER_ID.SELVER:
        return LINKS.SELVER_ORDERS;
      default:
        return null;
    }
  }

  public get contactLink() {
    switch (this.tenantId) {
      case RETAILER_ID.SELVER:
        return LINKS.SELVER_CONTACT;
      default:
        return LINKS.LXM_CONTATCT;
    }
  }

  public navigateQuickAction(url, actions?: string[]) {
    this._router
      .navigate([url], { queryParams: actions ? { action: actions } : {} })
      .then(() => {
        this.navigator.currentView = 0;
      });
  }

  public changeLanguage(selection: ILanguage) {
    this.locale.setLanguage(selection);

    this._userService.setPreferredLanguage(selection.id).subscribe();
  }

  public logout() {
    // this._authenticationService.logout();
    this._router.navigate(["/logout"]);
  }

  public switchTenant(tenantId: string) {
    this._authenticationService.switchTenant(tenantId);
  }

  public get adminLinkUrl() {
    if (this.appState.hasRight([UserAction.ManageSettings])) {
      return "/lxm-settings/profile";
    }

    if (this.appState.hasRight([UserAction.ViewUsers])) {
      return "/lxm-settings/users";
    }

    return "";
  }

  public isNavLinkActive(path: string): boolean {
    if (!path) return false;
    const pending = this._pendingNavigationPath;
    if (pending) {
      return this._pathMatchesUrl(path, pending);
    }
    return this._router.isActive(path, ROUTER_ACTIVE_OPTIONS);
  }

  private _pathMatchesUrl(path: string, urlPath: string): boolean {
    const p = urlPath.replace(/\/$/, "") || "/";
    const q = path.replace(/\/$/, "") || "/";
    return p === q || (q !== "/" && p.startsWith(q + "/"));
  }

  private _tenantSubscriptionPlanSubscription: Subscription;

  private _getTenantSubscriptionPlan() {
    const currentTenant = this.tenants.find((x) => x.id === this.tenantId);
    this.appState.tenantSubscriptionPlan.next(currentTenant?.subscriptionPlan);
  }

  public get isSmallerScreen() {
    return this.appState.clientWidth < BREAKPOINTS.lg;
  }

  private _checkForClientWidthNavigatorCollapse() {
    if (!this.navigator.collapsed && this.isSmallerScreen) {
      this.collapse(true);
    }
    this._cd.detectChanges();
  }

  ngOnInit() {
    const prop = "name";
    const tenants = this.appState.userSettings?.tenants?.sort((a, b) =>
      a[prop] > b[prop] ? 1 : a[prop] === b[prop] ? 0 : -1
    );
    this.form = new FormGroup({
      tenant: new FormControl(this.tenantId)
    });

    this.tenants = tenants;
    this.showTenantsFilter = tenants?.length > 10;

    this._getTenantSubscriptionPlan();
    this.form
      .get("tenant")
      .valueChanges.subscribe((val) => this._getTenantSubscriptionPlan());

    this._checkForClientWidthNavigatorCollapse();
    this.appState.updateApplicationSubject$.subscribe(() => {
      this._checkForClientWidthNavigatorCollapse();
    });

    this._routerEventsSubscription = this._router.events
      .pipe(
        filter(
          (e) =>
            e instanceof NavigationStart ||
            e instanceof NavigationEnd ||
            e instanceof NavigationCancel ||
            e instanceof NavigationError
        )
      )
      .subscribe((e) => {
        if (e instanceof NavigationStart) {
          this._pendingNavigationPath = e.url.split("?")[0] || "/";
          this._cd.markForCheck();
        } else {
          this._pendingNavigationPath = null;
          this._cd.markForCheck();
        }
      });
  }

  ngOnDestroy() {
    if (this._tenantSubscriptionPlanSubscription) {
      this._tenantSubscriptionPlanSubscription.unsubscribe();
    }
    if (this._routerEventsSubscription) {
      this._routerEventsSubscription.unsubscribe();
    }
  }
}
